import { Image } from '@/components/shared/Image';
import { useCollections } from '@/hooks/use-collections';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useEstimateGas } from '@/hooks/useEstimateGas';
import { wagmiConfig } from '@/lib/wagmi';
import { Button, Flex, Heading, Spinner, Text } from '@chakra-ui/react';
import { compareAddresses } from '@onbeam/utils';
import { waitForTransactionReceipt } from '@wagmi/core';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { Dispatch, SetStateAction, useState } from 'react';
import { toast } from 'sonner';
import { Address, encodeFunctionData, erc721Abi } from 'viem';
import { useAccount, useSwitchChain, useWalletClient } from 'wagmi';
import { TransferState } from './TransferBulk.types';

type Props = {
  unapprovedContracts: string[];
  setTransferState: Dispatch<SetStateAction<TransferState>>;
};

export const TransferBulkApprovals = ({ unapprovedContracts, setTransferState }: Props) => {
  const { t } = useTranslation('account');
  const [isApproving, setApproving] = useState(false);
  const [hasError, setError] = useState(false);
  const [currentContractIndex, setCurrentContractIndex] = useState(0);
  const marketplaceChain = useMarketplaceChain();
  const { chain } = useAccount();
  const { switchChain } = useSwitchChain();
  const { data: walletClient } = useWalletClient({ chainId: marketplaceChain.id });
  const { userAddress } = useAuthentication();

  const { data: collectionsData } = useCollections({ contract: unapprovedContracts });
  const currentCollection = collectionsData?.collections?.find(collection =>
    compareAddresses(collection.id, unapprovedContracts[currentContractIndex]),
  );

  // Estimate gas based on the first contract and multiply (address existence checked later)
  const { deficit: gasDeficit, showGasWarning } = useEstimateGas(
    {
      address: unapprovedContracts[0] as Address,
      abi: erc721Abi,
      functionName: 'setApprovalForAll' as const,
      args: [marketplaceChain.contracts?.tokenBeamer?.address ?? '0x', true] as const,
    },
    {
      multiplier: unapprovedContracts.length,
      isEnabled: !!marketplaceChain.contracts?.tokenBeamer?.address,
    },
  );

  const handleWriteContract = async (index: number) => {
    setError(false);
    setApproving(true);
    const address = unapprovedContracts[index];
    const tokenBeamerAddress = marketplaceChain.contracts?.tokenBeamer?.address;

    if (!address || !walletClient || !tokenBeamerAddress) {
      console.error('Missing required data for approval', {
        address,
        walletClient,
        tokenBeamerAddress,
      });
      toast.error(t('assets.bulk-transfer.approve.error'));
      setError(true);
      setApproving(false);
      return;
    }

    // Define params here now that we know tokenBeamerAddress is defined
    const params = {
      abi: erc721Abi,
      functionName: 'setApprovalForAll' as const,
      args: [tokenBeamerAddress, true] as const,
    };

    try {
      const hash = await walletClient.sendTransaction({
        chain: marketplaceChain,
        account: userAddress!,
        to: address as Address,
        data: encodeFunctionData(params),
      });

      const receipt = await waitForTransactionReceipt(wagmiConfig, {
        chainId: marketplaceChain.id,
        hash,
      });

      if (receipt.status !== 'success') throw new Error('Transaction failed');

      setCurrentContractIndex(previousIndex => {
        if (previousIndex === unapprovedContracts.length - 1) {
          setTransferState(TransferState.TRANSFER);
          return previousIndex;
        }
        return previousIndex + 1;
      });
    } catch {
      toast.error(t('assets.bulk-transfer.approve.error'));
      setError(true);
    } finally {
      setApproving(false);
    }
  };

  if (!currentCollection)
    return (
      <>
        <Heading fontSize="2xl" fontWeight="normal" mb="space.12">
          {t('assets.bulk-transfer.approve.title')}
        </Heading>
        <Flex h={rem(305)} color="gray.6" align="center" justify="center">
          <Spinner />
        </Flex>
      </>
    );

  return (
    <>
      <Heading fontSize="2xl" fontWeight="normal" mb="space.12">
        {t('assets.bulk-transfer.approve.title')}
      </Heading>
      <Flex direction="column" align="center">
        <Flex direction="column" align="center" gap="space.8" pt="space.24" pb="space.16">
          <Text color="gray.6" fontSize="sm" mb="space.12">
            {t('assets.bulk-transfer.approve.progress', {
              current: currentContractIndex + 1,
              count: unapprovedContracts.length,
            })}
          </Text>
          <Image
            src={currentCollection.image}
            alt={`${currentCollection.name} logo`}
            width={128}
            height={128}
            boxSize={rem(128)}
            objectFit="cover"
            bg="black"
            rounded="full"
            border="1px solid"
            borderColor="gray.4"
          />
          <Text>{currentCollection.name}</Text>
        </Flex>
        {showGasWarning && (
          <Flex py="space.12" px="space.18" rounded="radius.8" color="red" bg="darkRed">
            <Text fontSize="xs">
              {t('assets.bulk-transfer.approve.gas-warning', {
                count: unapprovedContracts.length,
                deficit: gasDeficit,
                currency: marketplaceChain.nativeCurrency.symbol,
              })}
            </Text>
          </Flex>
        )}
        <Flex w="full" gap="space.24" mt="space.16">
          <Button onClick={() => setTransferState(TransferState.IDLE)} w="full" variant="secondary">
            {t('assets.bulk-transfer.approve.cta.back')}
          </Button>
          {chain?.id === marketplaceChain.id ? (
            <Button
              onClick={() => handleWriteContract(currentContractIndex)}
              w="full"
              isLoading={isApproving}
            >
              {hasError
                ? t('assets.bulk-transfer.approve.cta.retry')
                : t('assets.bulk-transfer.approve.cta.continue')}
            </Button>
          ) : (
            <Button onClick={() => switchChain?.({ chainId: marketplaceChain.id })} w="full">
              {t('assets.bulk-transfer.approve.cta.switch-chain', { chain: marketplaceChain.name })}
            </Button>
          )}
        </Flex>
      </Flex>
    </>
  );
};
